import { ChevronDown, Flag, Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

interface GeneralInfoSectionProps {
  formData: any;
  setFormData: (data: any) => void;
  projects: any[];
  zones: any[];
  approvers: any[];
  profile: any;
}

export function GeneralInfoSection({ formData, setFormData, projects, zones, approvers, profile }: GeneralInfoSectionProps) {
  return (
    <section className="space-y-6">
      <h2 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide uppercase">Thông tin chung</h2>
      
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label className="sm:text-sm text-xs text-slate-700">Loại đề xuất <span className="text-rose-500">*</span></label>
          <div className="flex gap-2">
            <button 
              type="button"
              onClick={() => setFormData((prev: any) => ({ ...prev, requestType: 'thi_cong' }))}
              className={cn(
                "flex flex-1 items-center gap-2 rounded-md border h-11 px-3 transition-all sm:text-sm text-xs",
                formData.requestType === 'thi_cong' ? "border-emerald-600 bg-emerald-50/50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className={cn("flex h-4 w-4 items-center justify-center rounded-full border", formData.requestType === 'thi_cong' ? "border-emerald-600" : "border-slate-300")}>
                {formData.requestType === 'thi_cong' && <div className="h-2 w-2 rounded-full bg-emerald-600"></div>}
              </div>
              Thi công
            </button>
            <button 
              type="button"
              onClick={() => setFormData((prev: any) => ({ ...prev, requestType: 'bao_duong' }))}
              className={cn(
                "flex flex-1 items-center gap-2 rounded-md border h-11 px-3 transition-all sm:text-sm text-xs",
                formData.requestType === 'bao_duong' ? "border-emerald-600 bg-emerald-50/50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className={cn("flex h-4 w-4 items-center justify-center rounded-full border", formData.requestType === 'bao_duong' ? "border-emerald-600" : "border-slate-300")}>
                {formData.requestType === 'bao_duong' && <div className="h-2 w-2 rounded-full bg-emerald-600"></div>}
              </div>
              Bảo dưỡng
            </button>
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Dự án / Công việc <span className="text-rose-500">*</span></label>
          <div className="relative">
            <select 
              value={formData.projectId}
              onChange={e => setFormData((prev: any) => ({ ...prev, projectId: e.target.value }))}
              className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
            >
              <option value="">Chọn dự án...</option>
              {projects.map((p: any) => (
                <option key={p.id} value={p.id}>[{p.code}] {p.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Khu vực <span className="text-rose-500">*</span></label>
          <div className="relative">
            <select 
              value={formData.zoneId}
              onChange={e => setFormData((prev: any) => ({ ...prev, zoneId: e.target.value }))}
              className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
            >
              <option value="">Chọn khu vực...</option>
              {zones.map((z: any) => (
                <option key={z.id} value={z.id}>{z.code ? `[${z.code}] ` : ''}{z.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2 md:col-span-2 lg:col-span-2">
          <label className="sm:text-sm text-xs text-slate-700">Tiêu đề đề xuất <span className="text-rose-500">*</span></label>
          <div className="relative">
            <input 
              type="text" 
              placeholder="Nhập tiêu đề ngắn gọn về nội dung đề xuất"
              value={formData.title}
              onChange={e => setFormData((prev: any) => ({ ...prev, title: e.target.value }))}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 pr-16 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all placeholder:text-slate-400"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 sm:text-sm text-xs text-slate-400">{formData.title.length}/200</span>
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Ưu tiên <span className="text-rose-500">*</span></label>
          <div className="relative">
            <select 
              value={formData.priority}
              onChange={e => {
                const priority = e.target.value;
                setFormData((prev: any) => ({ 
                  ...prev, 
                  priority, 
                  isUrgent: priority === 'high' || priority === 'urgent' 
                }));
              }}
              className={cn(
                "h-11 w-full appearance-none rounded-md border bg-white px-3 pl-10 pr-10 sm:text-sm text-xs focus:outline-none transition-all",
                formData.priority === 'urgent' ? 'border-red-300 focus:border-red-500 text-red-700' :
                formData.priority === 'high' ? 'border-amber-300 focus:border-amber-500 text-amber-600' :
                formData.priority === 'low' ? 'border-slate-200 focus:border-slate-400 text-slate-500' :
                'border-slate-200 focus:border-emerald-500 text-slate-900'
              )}
            >
              <option value="low">Thấp</option>
              <option value="normal">Trung bình</option>
              <option value="high">Cao</option>
              <option value="urgent">Khẩn cấp</option>
            </select>
            <Flag className={cn(
              "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none", 
              formData.priority === 'urgent' ? "text-red-500" : 
              formData.priority === 'high' ? "text-amber-500" : 
              formData.priority === 'low' ? "text-slate-400" :
              "text-emerald-500"
            )} />
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2">
          <label className="sm:text-sm text-xs text-slate-700">Người đề xuất <span className="text-rose-500">*</span></label>
          <div className="flex h-11 w-full items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 opacity-80">
            <img src={profile?.avatar_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(profile?.full_name || 'A')} alt="Avatar" className="h-6 w-6 rounded-full" />
            <span className="sm:text-sm text-xs text-slate-700">{profile?.full_name || 'Đang tải...'}</span>
          </div>
        </div>

        <div className="space-y-2">
          <label className="sm:text-sm text-xs text-slate-700">Chức vụ</label>
          <input 
            type="text" 
            value={profile?.job_title || 'Chưa cập nhật'}
            disabled
            className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs text-slate-500 focus:outline-none opacity-80"
          />
        </div>

        <div className="space-y-2">
          <label className="sm:text-sm text-xs text-slate-700">Ngày đề xuất <span className="text-rose-500">*</span></label>
          <div className="relative">
            <input 
              type="text" 
              value={new Date().toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              disabled
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
            />
            <CalendarIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Người phê duyệt <span className="text-rose-500">*</span></label>
          <div className="relative">
            <select 
              value={formData.relatedUserId}
              onChange={e => setFormData((prev: any) => ({ ...prev, relatedUserId: e.target.value }))}
              className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-400 focus:border-emerald-500 focus:outline-none transition-all"
            >
              <option value="">Chọn người phê duyệt</option>
              {approvers.map((u: any) => (
                <option key={u.id} value={u.id}>{u.full_name} {u.job_title ? `(${u.job_title})` : ''}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Hạn cần phản hồi</label>
          <div className="relative">
            <input 
              type="date" 
              value={formData.deadlineDate}
              onChange={e => setFormData((prev: any) => ({ ...prev, deadlineDate: e.target.value }))}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        <div className="space-y-2 lg:col-span-1">
          <label className="sm:text-sm text-xs text-slate-700">Hạn dự kiến thực hiện</label>
          <div className="relative">
            <input 
              type="date" 
              value={formData.expectedDate}
              onChange={e => setFormData((prev: any) => ({ ...prev, expectedDate: e.target.value }))}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
